"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AIAssistStateManager = void 0;
const chatSettings_types_1 = require("@front/types/chatSettings.types");
class AIAssistStateManager {
    /**
     * Create initial component state
     * @param storageService Storage service for settings
     * @returns Initial state
     */
    static createInitialState(storageService) {
        return {
            entries: [],
            activeEntry: null,
            minimized: true,
            thinking: false,
            proposedKnowledgeIds: [],
            isLLMStreaming: false,
            currentSuggestions: [],
            isEnabled: storageService ? this.checkAssistantEnabled(storageService) : true
        };
    }
    /**
     * Check if AI Assistant is enabled from settings
     * @param storageService Storage service instance
     * @returns True if enabled
     */
    static checkAssistantEnabled(storageService) {
        // Handle case where storageService is not yet available
        if (!storageService || typeof storageService.get !== 'function') {
            return true; // Default to enabled if storage service not available
        }
        const savedSettings = storageService.get('ai.chatSettings');
        let settings = chatSettings_types_1.DEFAULT_CHAT_SETTINGS;
        if (savedSettings) {
            try {
                settings = JSON.parse(savedSettings);
                // Merge with defaults to ensure all properties exist
                settings = { ...chatSettings_types_1.DEFAULT_CHAT_SETTINGS, ...settings };
            }
            catch (e) {
                // Error parsing settings, use defaults
            }
        }
        return settings.enableAIAssistant !== false; // Default to true if not set
    }
    /**
     * Update enabled state
     * @param currentState Current component state
     * @param storageService Storage service instance
     * @returns Updated state
     */
    static updateEnabledState(currentState, storageService) {
        return {
            ...currentState,
            isEnabled: storageService ? this.checkAssistantEnabled(storageService) : currentState.isEnabled
        };
    }
    /**
     * Add entry to state
     * @param currentState Current component state
     * @param entry Entry to add
     * @param autoSelect Whether to auto-select the entry
     * @returns Updated state
     */
    static addEntry(currentState, entry, autoSelect = false) {
        const newEntries = [...currentState.entries, entry];
        return {
            ...currentState,
            entries: newEntries,
            activeEntry: autoSelect ? entry : currentState.activeEntry
        };
    }
    /**
     * Remove entry from state
     * @param currentState Current component state
     * @param entryToRemove Entry to remove
     * @returns Updated state
     */
    static removeEntry(currentState, entryToRemove) {
        const newEntries = currentState.entries.filter(entry => entry !== entryToRemove);
        return {
            ...currentState,
            entries: newEntries,
            activeEntry: currentState.activeEntry === entryToRemove ? null : currentState.activeEntry
        };
    }
    /**
     * Clear all entries
     * @param currentState Current component state
     * @returns Updated state
     */
    static clearEntries(currentState) {
        return {
            ...currentState,
            entries: [],
            activeEntry: null
        };
    }
    /**
     * Set active entry
     * @param currentState Current component state
     * @param entry Entry to set as active
     * @returns Updated state
     */
    static setActiveEntry(currentState, entry) {
        return {
            ...currentState,
            activeEntry: entry
        };
    }
    /**
     * Select entry by index
     * @param currentState Current component state
     * @param index Index of entry to select
     * @returns Updated state
     */
    static selectEntryByIndex(currentState, index) {
        if (index >= 0 && index < currentState.entries.length) {
            return {
                ...currentState,
                activeEntry: currentState.entries[index]
            };
        }
        return currentState;
    }
    /**
     * Set thinking state
     * @param currentState Current component state
     * @param thinking Whether component is thinking
     * @returns Updated state
     */
    static setThinking(currentState, thinking) {
        return {
            ...currentState,
            thinking
        };
    }
    /**
     * Set minimized state
     * @param currentState Current component state
     * @param minimized Whether component is minimized
     * @returns Updated state
     */
    static setMinimized(currentState, minimized) {
        return {
            ...currentState,
            minimized
        };
    }
    /**
     * Toggle minimized state
     * @param currentState Current component state
     * @returns Updated state
     */
    static toggleMinimized(currentState) {
        return {
            ...currentState,
            minimized: !currentState.minimized
        };
    }
    /**
     * Set LLM streaming state
     * @param currentState Current component state
     * @param streaming Whether LLM is streaming
     * @returns Updated state
     */
    static setLLMStreaming(currentState, streaming) {
        return {
            ...currentState,
            isLLMStreaming: streaming
        };
    }
    /**
     * Set proposed knowledge IDs
     * @param currentState Current component state
     * @param knowledgeIds Array of knowledge IDs
     * @returns Updated state
     */
    static setProposedKnowledgeIds(currentState, knowledgeIds) {
        return {
            ...currentState,
            proposedKnowledgeIds: knowledgeIds
        };
    }
    /**
     * Clear proposed knowledge IDs
     * @param currentState Current component state
     * @returns Updated state
     */
    static clearProposedKnowledgeIds(currentState) {
        return {
            ...currentState,
            proposedKnowledgeIds: []
        };
    }
    /**
     * Set current suggestions
     * @param currentState Current component state
     * @param suggestions Array of suggestions
     * @returns Updated state
     */
    static setCurrentSuggestions(currentState, suggestions) {
        return {
            ...currentState,
            currentSuggestions: suggestions
        };
    }
    /**
     * Clear current suggestions
     * @param currentState Current component state
     * @returns Updated state
     */
    static clearCurrentSuggestions(currentState) {
        return {
            ...currentState,
            currentSuggestions: [],
            activeEntry: null
        };
    }
    /**
     * Handle entries change (auto-selection logic)
     * @param oldEntries Previous entries
     * @param newEntries New entries
     * @param currentState Current component state
     * @returns Updated state
     */
    static handleEntriesChange(oldEntries, newEntries, currentState) {
        // Auto-select the last entry when a new one is added, but only if it doesn't have suggestions
        if (newEntries.length > 0 && newEntries.length > oldEntries.length) {
            const lastEntry = newEntries[newEntries.length - 1];
            // Only auto-select if the entry doesn't have actions/suggestions
            if (!lastEntry.actions || lastEntry.actions.length === 0) {
                return {
                    ...currentState,
                    activeEntry: lastEntry
                };
            }
        }
        return currentState;
    }
    /**
     * Get state statistics
     * @param state Component state
     * @returns State statistics
     */
    static getStateStats(state) {
        var _a;
        return {
            entryCount: state.entries.length,
            activeEntryType: ((_a = state.activeEntry) === null || _a === void 0 ? void 0 : _a.type) || null,
            suggestionCount: state.currentSuggestions.length,
            knowledgeCount: state.proposedKnowledgeIds.length,
            isEnabled: state.isEnabled,
            isMinimized: state.minimized,
            isThinking: state.thinking,
            isStreaming: state.isLLMStreaming
        };
    }
    /**
     * Validate state object
     * @param state State to validate
     * @returns True if state is valid
     */
    static isValidState(state) {
        return state &&
            Array.isArray(state.entries) &&
            typeof state.minimized === 'boolean' &&
            typeof state.thinking === 'boolean' &&
            Array.isArray(state.proposedKnowledgeIds) &&
            typeof state.isLLMStreaming === 'boolean' &&
            Array.isArray(state.currentSuggestions) &&
            typeof state.isEnabled === 'boolean';
    }
    /**
     * Merge state updates
     * @param currentState Current state
     * @param updates Partial state updates
     * @returns Updated state
     */
    static mergeState(currentState, updates) {
        return {
            ...currentState,
            ...updates
        };
    }
    /**
     * Reset state to initial values
     * @param storageService Storage service for checking enabled state
     * @returns Reset state
     */
    static resetState(storageService) {
        return this.createInitialState(storageService);
    }
}
exports.AIAssistStateManager = AIAssistStateManager;
